import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import Moment from "moment";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import useThemeColor from "../../hooks/useThemeColor";
import useDarkMode from "../../hooks/useDarkMode";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import MUITimePicker from "../../components/CustomComponents/MUITimePicker/MUITimePicker";
import "../order/ManageOrderFilters.css";
import "./zReport.css";

const defaultLocation = { value: "all", label: "All Locations" };

const ZReport = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = getCompCurrency();
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [location, setLocation] = useState(defaultLocation);
  const [zReportData, setZReportData] = useState(null);
  const [zReportHistory, setZReportHistory] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewLocation, setPreviewLocation] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAutoScheduleModal, setShowAutoScheduleModal] = useState(false);
  const [selectedZReport, setSelectedZReport] = useState(null);

  const [autoScheduleEnabled, setAutoScheduleEnabled] = useState(false);
  const [scheduleLocation, setScheduleLocation] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [runTimeValue, setRunTimeValue] = useState(Moment("23:00", "HH:mm"));
  const scheduleRequestIdRef = useRef(0);

  // Pagination and filtering states
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState({ value: "ToDateTime", label: "Date" });
  const [sortOrder, setSortOrder] = useState({ value: "DESC", label: "DESC" });

  const sortByOptions = [
    { value: "ToDateTime", label: "Date" },
    { value: "GrossSales", label: "Gross Sales" },
    { value: "TotalOrders", label: "Total Orders" },
    { value: "CashPosition", label: "Cash Position" },
  ];

  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return `${getCurrencySymbol(compCurrency)}0.00`;
    return `${getCurrencySymbol(compCurrency)}${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const parentIdForLocation = isMain && CompID != null ? (typeof CompID === "number" ? CompID : parseInt(CompID, 10)) : null;
  const companyName = profileData?.compName ?? profileData?.CompName ?? "Parent Branch";
  const locationOptions = useMemo(() => {
    if (locations.length > 0) {
      return [{ value: "all", label: "All Locations" }, ...locations];
    }
    if (isMain && parentIdForLocation != null && !isNaN(parentIdForLocation)) {
      return [{ value: "all", label: "All Locations" }, { value: parentIdForLocation, label: companyName }];
    }
    return [{ value: "all", label: "All Locations" }];
  }, [locations, isMain, parentIdForLocation, companyName]);

  const getLocationFilterValue = useCallback(() => {
    if (!isMain || location?.value === "all" || location?.value == null) return null;
    const locValue = typeof location.value === "number" ? location.value : parseInt(location.value, 10);
    return !isNaN(locValue) && locValue > 0 ? locValue : null;
  }, [isMain, location]);

  const branchLocationOptions = useMemo(
    () => locationOptions.filter((opt) => opt.value !== "all"),
    [locationOptions]
  );

  const scheduleLocationOptions = branchLocationOptions;
  const previewLocationOptions = branchLocationOptions;

  const getPreviewLocationFilterValue = useCallback((branchOption) => {
    if (!isMain) return null;
    const activeBranch = branchOption || previewLocation || previewLocationOptions[0] || null;
    if (!activeBranch?.value) return null;
    const parentCompIdNum = typeof CompID === "number" ? CompID : parseInt(CompID, 10);
    const locValue = typeof activeBranch.value === "number" ? activeBranch.value : parseInt(activeBranch.value, 10);
    if (isNaN(locValue) || locValue <= 0 || locValue === parentCompIdNum) return null;
    return locValue;
  }, [isMain, previewLocation, previewLocationOptions, CompID]);

  const getScheduleLocationFilterValue = useCallback(() => {
    if (!isMain) return null;
    const activeLocation = scheduleLocation || scheduleLocationOptions[0] || null;
    if (!activeLocation?.value) return null;
    const locValue = typeof activeLocation.value === "number" ? activeLocation.value : parseInt(activeLocation.value, 10);
    return !isNaN(locValue) && locValue > 0 ? locValue : null;
  }, [isMain, scheduleLocation, scheduleLocationOptions]);

  const requiresScheduleLocation = useCallback(() => {
    if (!isMain) return false;
    return getScheduleLocationFilterValue() == null;
  }, [isMain, getScheduleLocationFilterValue]);

  const buildHistoryParams = useCallback((overrides = {}) => {
    const params = {
      CompID,
      PageNumber: pageNumber,
      PageSize: Number(pageSize?.value ?? pageSize) || 50,
      Search: search?.trim() || "",
      SortBy: sortBy?.value || sortBy,
      SortOrder: sortOrder?.value || sortOrder,
      ...overrides,
    };

    const locationFilter = getLocationFilterValue();
    if (locationFilter != null) {
      params.LocationFilter = locationFilter;
    }

    return params;
  }, [CompID, pageNumber, pageSize, search, sortBy, sortOrder, getLocationFilterValue]);

  const requiresSpecificLocation = useCallback(() => {
    if (!isMain) return false;
    return location?.value === "all" || location?.value == null;
  }, [isMain, location]);

  const fetchLocations = useCallback(async () => {
    if (!isMain || !CompID) return;
    try {
      setLoadingLocations(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, { params: { CompID } });
      const locationData = response.data?.data ?? response.data ?? [];
      const arr = Array.isArray(locationData) ? locationData : [];
      const parentId = typeof CompID === "number" ? CompID : parseInt(CompID, 10);
      const base = [{ value: parentId, label: companyName }];
      const branchOpts = arr
        .filter((loc) => {
          const pid = loc.ParentComID ?? loc.parentComID;
          const id = loc.CompID ?? loc.compID;
          const pidNum = pid != null ? (typeof pid === "number" ? pid : parseInt(pid, 10)) : null;
          const idNum = id != null ? (typeof id === "number" ? id : parseInt(id, 10)) : null;
          return idNum != null && !isNaN(idNum) && pidNum === parentId && idNum !== parentId;
        })
        .map((loc) => ({
          value: (() => {
            const id = loc.CompID ?? loc.compID;
            return typeof id === "number" ? id : parseInt(id, 10);
          })(),
          label: String(loc.CompName ?? loc.compName ?? "").trim() || `Branch ${loc.CompID ?? loc.compID ?? ""}`,
        }))
        .filter((o) => o.value != null && !isNaN(o.value));
      setLocations([...base, ...branchOpts]);
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Error loading locations");
    } finally {
      setLoadingLocations(false);
    }
  }, [CompID, isMain, companyName]);

  useEffect(() => {
    if (isMain) {
      fetchLocations();
    }
  }, [isMain, fetchLocations]);

  const applyAutoScheduleFormData = useCallback((data) => {
    const runTime = data?.runTime ?? data?.RunTime ?? "23:00";
    setAutoScheduleEnabled(!!(data?.isEnabled ?? data?.IsEnabled));
    const parsedTime = Moment(runTime, ["HH:mm", "H:mm", "hh:mm A", "h:mm A", "h A", "ha"]);
    setRunTimeValue(parsedTime.isValid() ? parsedTime : Moment("23:00", "HH:mm"));
  }, []);

  const loadAutoScheduleForBranch = useCallback(async (branchOption) => {
    if (!CompID) return;

    const parentCompIdNum = typeof CompID === "number" ? CompID : parseInt(CompID, 10);
    if (isMain && !branchOption?.value) return;

    const requestId = ++scheduleRequestIdRef.current;

    try {
      setScheduleLoading(true);
      const params = { CompID };

      if (isMain && branchOption?.value) {
        const locValue = typeof branchOption.value === "number" ? branchOption.value : parseInt(branchOption.value, 10);
        if (!isNaN(locValue) && locValue > 0 && locValue !== parentCompIdNum) {
          params.LocationFilter = locValue;
        }
      }

      const response = await axiosInstance.get(APIs.GET_Z_REPORT_AUTO_SCHEDULE, { params });
      if (requestId !== scheduleRequestIdRef.current) return;

      const isSuccess = response.data?.status ?? response.data?.Status;
      if (isSuccess) {
        const data = response.data.data || response.data.Data || {};
        applyAutoScheduleFormData(data);
      } else {
        applyAutoScheduleFormData({ isEnabled: false, runTime: "23:00" });
      }
    } catch (err) {
      if (requestId !== scheduleRequestIdRef.current) return;
      applyAutoScheduleFormData({ isEnabled: false, runTime: "23:00" });
    } finally {
      if (requestId === scheduleRequestIdRef.current) {
        setScheduleLoading(false);
      }
    }
  }, [CompID, isMain, applyAutoScheduleFormData]);

  const loadAutoScheduleRef = useRef(loadAutoScheduleForBranch);
  loadAutoScheduleRef.current = loadAutoScheduleForBranch;

  const handleOpenAutoScheduleModal = () => {
    const defaultBranch = isMain
      ? scheduleLocation || scheduleLocationOptions[0] || null
      : null;

    if (isMain && defaultBranch && !scheduleLocation) {
      setScheduleLocation(defaultBranch);
    }

    setShowAutoScheduleModal(true);
  };

  const handleScheduleBranchChange = (option) => {
    setScheduleLocation(option);
  };

  const scheduleBranchKey = scheduleLocation?.value ?? scheduleLocationOptions[0]?.value ?? null;

  useEffect(() => {
    if (!showAutoScheduleModal) {
      setScheduleLoading(false);
    }
  }, [showAutoScheduleModal]);

  useEffect(() => {
    if (!showAutoScheduleModal) return;
    if (isMain && scheduleLocationOptions.length === 0) return;

    const branch = scheduleLocation
      || scheduleLocationOptions.find((opt) => opt.value === scheduleBranchKey)
      || scheduleLocationOptions[0];

    loadAutoScheduleRef.current(branch);
  }, [showAutoScheduleModal, isMain, scheduleBranchKey, scheduleLocationOptions.length]);

  const handleSaveAutoSchedule = useCallback(async () => {
    if (!CompID) return;
    if (requiresScheduleLocation()) {
      showErrorToast("Please select a specific branch to configure auto Z-Report");
      return;
    }

    try {
      setSavingSchedule(true);
      const parentCompIdNum = typeof CompID === "number" ? CompID : parseInt(CompID, 10);
      const locationFilter = getScheduleLocationFilterValue();
      const runTime = runTimeValue?.isValid()
        ? runTimeValue.format("HH:mm")
        : "23:00";

      const payload = {
        CompID,
        IsEnabled: autoScheduleEnabled,
        RunTime: runTime,
      };
      if (locationFilter != null && locationFilter !== parentCompIdNum) {
        payload.LocationFilter = locationFilter;
      }

      const response = await axiosInstance.post(APIs.POST_UPDATE_Z_REPORT_AUTO_SCHEDULE, payload);

      const isSuccess = response.data?.status ?? response.data?.Status;
      if (isSuccess) {
        showSuccessToast("Auto Z-Report schedule saved successfully");
        setShowAutoScheduleModal(false);
      } else {
        showErrorToast(response.data?.message || response.data?.Message || "Failed to save auto Z-Report schedule");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to save auto Z-Report schedule");
    } finally {
      setSavingSchedule(false);
    }
  }, [CompID, autoScheduleEnabled, getScheduleLocationFilterValue, requiresScheduleLocation, runTimeValue]);

  const headerButtonStyle = {
    backgroundColor: themeColor,
    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
    border: "none",
    color: "white",
    borderRadius: "8px",
    padding: "8px 16px",
    fontSize: "14px",
    fontWeight: "600",
    transition: "all 0.3s ease",
    boxShadow: `0 2px 8px ${themeColor}40`,
  };

  const handleHeaderButtonHover = (e, isEnter) => {
    if (isEnter) {
      e.currentTarget.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
      e.currentTarget.style.transform = "translateY(-1px)";
      e.currentTarget.style.boxShadow = `0 4px 12px ${themeColor}60`;
    } else {
      e.currentTarget.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = `0 2px 8px ${themeColor}40`;
    }
  };

  const fetchZReportPreview = useCallback(async (branchOption) => {
    if (!CompID) return;

    try {
      setPreviewLoading(true);
      const previewParams = { CompID };
      const locationFilter = getPreviewLocationFilterValue(branchOption);
      if (locationFilter != null) previewParams.LocationFilter = locationFilter;

      const response = await axiosInstance.get(APIs.GET_Z_REPORT_PREVIEW, {
        params: previewParams,
      });

      if (response.data?.status ?? response.data?.Status) {
        setZReportData(response.data.data);
      } else {
        setZReportData(null);
        showErrorToast(response.data.message || "Failed to fetch Z-Report preview");
      }
    } catch (err) {
      setZReportData(null);
      showErrorToast("Failed to load Z-Report preview");
      console.error('Error fetching Z-Report:', err);
    } finally {
      setPreviewLoading(false);
    }
  }, [CompID, getPreviewLocationFilterValue]);

  const runZReport = useCallback(async () => {
    if (!CompID) return;

    if (!window.confirm("Are you sure you want to run the Z-Report? This will save the current period's data.")) {
      return;
    }

    try {
      setLoading(true);
      const runParams = { CompID };
      const locationFilter = getPreviewLocationFilterValue();
      if (locationFilter != null) runParams.LocationFilter = locationFilter;

      const response = await axiosInstance.post(APIs.RUN_Z_REPORT, null, {
        params: runParams,
      });

      if (response.data?.status ?? response.data?.Status) {
        showSuccessToast("Z-Report generated successfully!");
        await fetchZReportPreview(previewLocation); // Refresh preview
        // Refresh history by calling fetchZReportHistory directly
        const historyResponse = await axiosInstance.get(APIs.GET_Z_REPORT_HISTORY_PAGINATED, {
          params: buildHistoryParams(),
        });
        if (historyResponse.data?.status ?? historyResponse.data?.Status) {
          setZReportHistory(historyResponse.data.data || historyResponse.data.Data || []);
          setTotalPages(historyResponse.data.totalPages ?? historyResponse.data.TotalPages ?? 0);
          setTotalRecords(historyResponse.data.totalRecords ?? historyResponse.data.TotalRecords ?? 0);
        }
        setShowPreviewModal(false); // Close modal after running
      } else {
        showErrorToast(response.data.message || "Failed to generate Z-Report");
      }
    } catch (err) {
      showErrorToast("Failed to generate Z-Report");
      console.error('Error running Z-Report:', err);
    } finally {
      setLoading(false);
    }
  }, [CompID, fetchZReportPreview, buildHistoryParams, getPreviewLocationFilterValue, previewLocation]);

  const fetchZReportHistory = useCallback(async () => {
    if (!CompID) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_Z_REPORT_HISTORY_PAGINATED, {
        params: buildHistoryParams(),
      });

      const isSuccess = response.data?.status ?? response.data?.Status;
      if (isSuccess) {
        setZReportHistory(response.data.data || response.data.Data || []);
        setTotalPages(response.data.totalPages ?? response.data.TotalPages ?? 0);
        setTotalRecords(response.data.totalRecords ?? response.data.TotalRecords ?? 0);
      } else {
        showErrorToast(response.data?.message || response.data?.Message || "Failed to fetch Z-Report history");
        setZReportHistory([]);
        setTotalPages(0);
        setTotalRecords(0);
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || err.response?.data?.Message || "Failed to load Z-Report history");
      console.error('Error fetching Z-Report history:', err);
      setZReportHistory([]);
      setTotalPages(0);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [CompID, buildHistoryParams]);

  const clearFilters = () => {
    setSearch("");
    setSortBy({ value: "ToDateTime", label: "Date" });
    setSortOrder({ value: "DESC", label: "DESC" });
    setLocation(defaultLocation);
    setPageNumber(1);
  };

  useEffect(() => {
    fetchZReportHistory();
  }, [fetchZReportHistory]);

  const handleViewDetails = (report) => {
    setSelectedZReport(report);
    setShowDetailsModal(true);
  };

  const handleOpenPreview = () => {
    const defaultBranch = isMain
      ? previewLocation || previewLocationOptions[0] || null
      : null;

    if (isMain && defaultBranch) {
      setPreviewLocation(defaultBranch);
    }

    setShowPreviewModal(true);
    fetchZReportPreview(defaultBranch);
  };

  const handlePreviewBranchChange = (option) => {
    setPreviewLocation(option);
    fetchZReportPreview(option);
  };

  const handlePrint = async (zReportDataToPrint = null) => {
    if (!CompID) return;

    try {
      setLoading(true);
      const dataToPrint = zReportDataToPrint || zReportData || selectedZReport;

      if (!dataToPrint) {
        showErrorToast("No Z-Report data available to print");
        return;
      }

      const zReportId = dataToPrint.id && dataToPrint.id > 0 ? dataToPrint.id : null;
      const isPreviewPrint = !zReportId && dataToPrint === zReportData;

      const pdfParams = { CompID, ZReportID: zReportId };
      if (!zReportId) {
        const locationFilter = isPreviewPrint
          ? getPreviewLocationFilterValue()
          : getLocationFilterValue();
        if (locationFilter != null) pdfParams.LocationFilter = locationFilter;
      }

      const response = await axiosInstance.get(APIs.GENERATE_Z_REPORT_PDF, {
        params: pdfParams,
        responseType: 'blob'
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const fileName = zReportId
        ? `Z-Report-${zReportId}-${Moment(dataToPrint.toDateTime).format('YYYYMMDD-HHmmss')}.pdf`
        : `Z-Report-Preview-${Moment().format('YYYYMMDD-HHmmss')}.pdf`;

      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccessToast("PDF downloaded successfully");
    } catch (err) {
      showErrorToast("Failed to generate PDF");
      console.error('Error generating PDF:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate chart data for pie chart (VAT for deductions includes refunded orders: net = vat - vatRefunded)
  const getChartData = () => {
    if (!zReportData) return null;

    const netSales = zReportData.netSales || 0;
    const vat = (zReportData.vat ?? 0) - (zReportData.vatRefunded ?? 0);
    const totalDiscount = zReportData.discountAmount || 0;
    const totalCharges = (zReportData.serviceCharges || 0) +
      (zReportData.deliveryCharges || 0) +
      (zReportData.onlineCharges || 0) +
      (zReportData.extraCharges || 0);
    const tipAmount = zReportData.tipAmount || 0;
    const refundAmount = zReportData.refundAmount || 0;

    return {
      netSales,
      vat,
      totalDiscount,
      totalCharges,
      tipAmount,
      refundAmount
    };
  };

  const chartData = getChartData();
  const tableColumnCount = isMain ? 8 : 7;

  const getBranchName = (report, branchOption = null) => {
    const name = report?.compName ?? report?.CompName;
    if (name && String(name).trim()) return String(name).trim();
    if (branchOption?.label) return branchOption.label;
    const reportCompId = report?.compID ?? report?.CompID;
    const matched = branchLocationOptions.find((opt) => opt.value === reportCompId);
    return matched?.label ?? companyName;
  };

  const renderZReportContent = (data) => {
    if (!data) return null;

    // VAT = both completed and refunded orders (sum of both for total deductions)
    const vatCompleted = data.vat ?? 0;
    const vatRefunded = data.vatRefunded ?? 0;
    const vatTotal = vatCompleted + vatRefunded;
    const totalDeductions = vatTotal + (data.refundAmount ?? 0) + (data.discountAmount ?? 0);
    const netSalesDisplay = (data.grossSales ?? 0) - totalDeductions;

    // Calculate chart data from the passed data parameter
    const calculateChartData = (reportData) => {
      if (!reportData) return null;

      const netSales = reportData.netSales || 0;
      const vat = (reportData.vat ?? 0) - (reportData.vatRefunded ?? 0); // net VAT for chart
      const totalDiscount = reportData.discountAmount || 0;
      const totalCharges = (reportData.serviceCharges || 0) +
        (reportData.deliveryCharges || 0) +
        (reportData.onlineCharges || 0) +
        (reportData.extraCharges || 0);
      const tipAmount = reportData.tipAmount || 0;
      const refundAmount = reportData.refundAmount || 0;

      return {
        netSales,
        vat,
        totalDiscount,
        totalCharges,
        tipAmount,
        refundAmount
      };
    };

    const currentChartData = calculateChartData(data);
    // Use Gross Sales as the base for percentage calculations
    const grossSales = data.grossSales || 0;

    return (
      <div className={`z-report-content ${isDarkMode ? 'z-report-dark' : ''}`}>
        {/* Combined Sales Section */}
        <div 
          className="z-report-section combined-sales-section"
          style={{
            background: '#ffffff',
            boxShadow: `0 4px 20px ${themeColor}20`,
            border: `2px solid ${themeColor}`
          }}
        >
          <div className="sales-header" style={{ borderBottomColor: themeColor }}>
            <h3 style={{ color: themeColor }}>Z Report Summary</h3>
          </div>

          <div className="z-report-summary-grid">
            {/* Row 1: Gross Sales (col 1-4) | Deductions (center) | Net Sales (last 4 cols) */}
            <div className="z-report-summary-row z-report-summary-row-totals">
              <div className="z-report-summary-col gross-col">
                <h4 style={{ color: themeColor }}>Gross Sales</h4>
                <div className="z-report-summary-total" style={{ color: isDarkMode ? '#e9ecef' : '#333' }}>{formatCurrency(data.grossSales)}</div>
              </div>
              <div className="z-report-summary-col deductions-col">
                <h4 style={{ color: themeColor }}>Deductions</h4>
                <div className="z-report-summary-total deductions-total" style={{ color: '#dc3545' }}>{formatCurrency(totalDeductions)}</div>
                <div className="z-report-deductions-list">
                  <div className="z-report-deduction-item">
                    <span className="deduction-label" title="VAT on completed orders + VAT on refunded orders">VAT</span>
                    <span className="deduction-value">{formatCurrency(vatTotal)}</span>
                  </div>
                  <div className="z-report-deduction-item">
                    <span className="deduction-label">Refund Amount</span>
                    <span className="deduction-value">{formatCurrency(data.refundAmount || 0)}</span>
                  </div>
                  <div className="z-report-deduction-item">
                    <span className="deduction-label">Discount (incl. voucher)</span>
                    <span className="deduction-value">{formatCurrency(data.discountAmount || 0)}</span>
                  </div>
                </div>
              </div>
              <div className="z-report-summary-col net-col">
                <h4 style={{ color: themeColor }}>Net Sales</h4>
                <div className="z-report-summary-total" style={{ color: isDarkMode ? '#e9ecef' : '#333' }}>{formatCurrency(netSalesDisplay)}</div>
              </div>
            </div>
            {/* Row 2: Cash | Card | Online - net sales per payment method */}
            <div className="z-report-summary-row z-report-summary-row-payments">
              <div className="z-report-payment-card">
                <h4 style={{ color: themeColor }}>Cash</h4>
                <div className="z-report-payment-total">{formatCurrency(data.cash_NetSales ?? 0)}</div>
              </div>
              <div className="z-report-payment-card">
                <h4 style={{ color: themeColor }}>Card</h4>
                <div className="z-report-payment-total">{formatCurrency(data.card_NetSales ?? 0)}</div>
              </div>
              <div className="z-report-payment-card">
                <h4 style={{ color: themeColor }}>Online</h4>
                <div className="z-report-payment-total">{formatCurrency(data.online_NetSales ?? 0)}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="z-report-main-content">
          {/* Left Column - Charges and Amounts */}
          <div className="z-report-left-column">
            <div className="z-report-section-header" style={{ borderBottomColor: themeColor }}>
              <h4 style={{ color: themeColor }}>Charges & Discounts</h4>
            </div>
            <div className="z-report-details-section">
              <div className="detail-item" style={{ borderLeftColor: themeColor }}>
                <span className="label">Extra Charges:</span>
                <span className="value" style={{ color: themeColor }}>{formatCurrency(data.extraCharges)}</span>
              </div>
              <div className="detail-item" style={{ borderLeftColor: themeColor }}>
                <span className="label">Service Charges:</span>
                <span className="value" style={{ color: themeColor }}>{formatCurrency(data.serviceCharges)}</span>
              </div>
              <div className="detail-item" style={{ borderLeftColor: themeColor }}>
                <span className="label">Delivery Charges:</span>
                <span className="value" style={{ color: themeColor }}>{formatCurrency(data.deliveryCharges)}</span>
              </div>
              <div className="detail-item" style={{ borderLeftColor: themeColor }}>
                <span className="label">Tip Amount:</span>
                <span className="value" style={{ color: themeColor }}>{formatCurrency(data.tipAmount)}</span>
              </div>
              <div className="detail-item" style={{ borderLeftColor: themeColor }}>
                <span className="label">Vouchers Amount:</span>
                <span className="value" style={{ color: themeColor }}>{formatCurrency(data.voucherDiscountAmount)}</span>
              </div>
            </div>
          </div>

          {/* Right Column - Pie Chart */}
          <div className="z-report-right-column">
            <div className="z-report-section-header" style={{ borderBottomColor: themeColor }}>
              <h4 style={{ color: themeColor }}>Sales Breakdown</h4>
            </div>
            <div className="z-report-details-section">
              {currentChartData && grossSales > 0 && (
                <>
                  <div className="detail-item" style={{ borderLeftColor: themeColor }}>
                    <span className="label">Net Sales:</span>
                    <span className="value" style={{ color: themeColor }}>{grossSales > 0 ? ((netSalesDisplay / grossSales) * 100).toFixed(1) : '0.0'}%</span>
                  </div>
                  <div className="detail-item" style={{ borderLeftColor: themeColor }}>
                    <span className="label">VAT (Net):</span>
                    <span className="value" style={{ color: themeColor }}>{((currentChartData.vat / grossSales) * 100).toFixed(1)}%</span>
                  </div>
                  <div className="detail-item" style={{ borderLeftColor: themeColor }}>
                    <span className="label">Total Discount:</span>
                    <span className="value" style={{ color: themeColor }}>{((currentChartData.totalDiscount / grossSales) * 100).toFixed(1)}%</span>
                  </div>
                  <div className="detail-item" style={{ borderLeftColor: themeColor }}>
                    <span className="label">Total Charges:</span>
                    <span className="value" style={{ color: themeColor }}>{((currentChartData.totalCharges / grossSales) * 100).toFixed(1)}%</span>
                  </div>
                  <div className="detail-item" style={{ borderLeftColor: themeColor }}>
                    <span className="label">Tip Amount:</span>
                    <span className="value" style={{ color: themeColor }}>{((currentChartData.tipAmount / grossSales) * 100).toFixed(1)}%</span>
                  </div>
                  <div className="detail-item" style={{ borderLeftColor: themeColor }}>
                    <span className="label">Refunded Amount:</span>
                    <span className="value" style={{ color: themeColor }}>{((currentChartData.refundAmount / grossSales) * 100).toFixed(1)}%</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>



        {/* Bottom Section - Orders and Float */}
        <div className="z-report-bottom-section">
          <div className="z-report-orders-section">
            <div className="z-report-section-header">
              <h4>Total Orders: {data.totalOrders || 0}</h4>
            </div>
            <div className="float-details">
              <div className="float-item">
                <span className="label">Running Orders:</span>
                <span className="value">{data.runningOrders || 0}</span>
              </div>
              <div className="float-item">
                <span className="label">Completed Orders:</span>
                <span className="value">{data.completedOrders || 0}</span>
              </div>
              <div className="float-item">
                <span className="label">Refunded Orders:</span>
                <span className="value">{data.refundedOrders || 0}</span>
              </div>
              <div className="float-item">
                <span className="label">Cancelled Orders:</span>
                <span className="value">{data.cancelledOrders || 0}</span>
              </div>
              <div className="float-item">
                <span className="label">Cancelled Orders Amount:</span>
                <span className="value">{formatCurrency(data.cancelledOrdersAmount || 0)}</span>
              </div>
            </div>
          </div>

          <div className="z-report-float-section">
            <div className="z-report-section-header">
              <h4>Float</h4>
            </div>
            <div className="float-details">
              <div className="float-item">
                <span className="label">Opening Balance:</span>
                <span className="value">{formatCurrency(data.floatOpeningBalance || 0)}</span>
              </div>
              <div className="float-item">
                <span className="label">Withdraw:</span>
                <span className="value">{formatCurrency(data.floatWithdraw || 0)}</span>
              </div>
              <div className="float-item">
                <span className="label">Deposited:</span>
                <span className="value">{formatCurrency(data.floatDeposited || 0)}</span>
              </div>
              <div className="float-item">
                <span className="label">Petty Cash:</span>
                <span className="value">{formatCurrency(data.pettyCash || 0)}</span>
              </div>
              <div className="float-item closing-balance">
                <span className="label">Closing Balance:</span>
                <span className={`value ${(data.floatClosingBalance || 0) < 0 ? 'negative' : ''}`}>
                  {formatCurrency(data.floatClosingBalance || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Cash Position */}
        <div 
          className="z-report-cash-position"
          style={{
            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
            boxShadow: `0 4px 20px ${themeColor}40`,
            border: `2px solid ${themeColor}`
          }}
        >
          <h3 style={{ color: '#fff' }}>Cash Position: {formatCurrency(data.cashPosition || 0)}</h3>
        </div>
      </div>
    );
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Z-Report</li>
          </ol>
          <h1 className="page-header mb-0">Z-Report</h1>
        </div>
        <div className="ms-auto">
          <div className="d-flex justify-content-end align-items-center mb-3 gap-2">
            <button
              className="btn px-4"
              onClick={handleOpenAutoScheduleModal}
              style={headerButtonStyle}
              onMouseEnter={(e) => handleHeaderButtonHover(e, true)}
              onMouseLeave={(e) => handleHeaderButtonHover(e, false)}
            >
              <i className="fas fa-clock fa-lg me-2 ms-n2 text-white"></i>
              Auto Z-Report
            </button>
            <button
              className="btn px-4"
              onClick={handleOpenPreview}
              style={headerButtonStyle}
              onMouseEnter={(e) => handleHeaderButtonHover(e, true)}
              onMouseLeave={(e) => handleHeaderButtonHover(e, false)}
            >
              <i className="fas fa-eye fa-lg me-2 ms-n2 text-white"></i>
              Preview Z-Report
            </button>
          </div>
        </div>
      </div>

      <div className="mb-3 mt-2">
        <div className="card border-0">
          <div className="tab-content p-3">
            <div className="tab-pane fade show active" id="allTab">
              {/* Enhanced Filter Section */}
              <div className="mb-4">
                <div
                  className="card filter-card"
                  style={{
                    position: "relative",
                    zIndex: 1,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: `0 4px 20px ${themeColor}40`
                  }}
                >
                  <div className="card-body p-3">
                    <div className="row g-3 align-items-center">
                      {isMain && locationOptions.length > 1 && (
                        <div className="col-xl-3 col-lg-3 col-md-6">
                          <div className="filter-control">
                            <Select
                              value={location}
                              onChange={(option) => {
                                setLocation(option || defaultLocation);
                                setPageNumber(1);
                              }}
                              options={locationOptions}
                              styles={dropdownStyles}
                              isSearchable={false}
                              isLoading={loadingLocations}
                              placeholder="Location"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                            />
                          </div>
                        </div>
                      )}
                      {/* Search Field */}
                      <div className={`col-xl-${isMain && locationOptions.length > 1 ? "3" : "4"} col-lg-4 col-md-6`}>
                        <div className="search-input filter-control">
                          <input
                            type="text"
                            className="filter-input"
                            value={search}
                            onChange={(e) => {
                              setSearch(e.target.value);
                              setPageNumber(1);
                            }}
                            placeholder="Search by ID, Gross Sales, Net Sales, Total Orders..."
                            style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              color: '#333'
                            }}
                          />
                        </div>
                      </div>

                      {/* Order By Dropdown */}
                      <div className="col-xl-3 col-lg-3 col-md-6">
                        <div className="filter-control">
                          <select
                            className="form-control filter-input"
                            value={sortBy.value || sortBy}
                            onChange={(e) => {
                              const selected = sortByOptions.find(opt => opt.value === e.target.value);
                              setSortBy(selected || { value: e.target.value, label: e.target.value });
                              setPageNumber(1);
                            }}
                            style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              color: '#333',
                              height: '42px'
                            }}
                          >
                            {sortByOptions.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Sort Order Dropdown */}
                      <div className="col-xl-2 col-lg-2 col-md-6">
                        <div className="filter-control">
                          <SortOrderDropDownSelection
                            defaultValue={sortOrder}
                            onChange={(val) => {
                              setSortOrder(val);
                              setPageNumber(1);
                            }}
                          />
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="col-xl-3 col-lg-3 col-md-12">
                        <div className="d-flex gap-2">
                          <button
                            className="clear-btn"
                            onClick={clearFilters}
                            title="Clear all filters"
                            style={{
                              backgroundColor: 'rgba(0, 0, 0, 0.2)',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              color: 'white',
                              padding: '8px 16px',
                              borderRadius: '8px',
                              fontWeight: '600'
                            }}
                          >
                            <i className="fa fa-times me-1"></i>
                            Filter
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Table Section */}
              {zReportHistory.length === 0 && !loading ? (
                <div className="alert alert-info text-center mt-3">
                  <i className="fa fa-info-circle me-2"></i>
                  No Z-Reports found
                </div>
              ) : (
                <>
                  <div
                    className="table-responsive table-container"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <table className="table table-hover" style={{ position: "relative" }}>
                      <thead
                        className="bg-light sticky-header"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10000,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          {isMain && (
                            <th className="pt-2 pb-2">Branch</th>
                          )}
                          <th className="pt-2 pb-2">From Date</th>
                          <th className="pt-2 pb-2">To Date</th>
                          <th className="pt-2 pb-2 text-center">Gross Sales</th>
                          <th className="pt-2 pb-2 text-center">Net Sales</th>
                          <th className="pt-2 pb-2 text-center">Total Orders</th>
                          <th className="pt-2 pb-2 text-center">Cash Position</th>
                           
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {zReportHistory.length === 0 ? (
                          <tr>
                            <td colSpan={tableColumnCount} className="text-center">No Z-Reports found</td>
                          </tr>
                        ) : (
                          zReportHistory.map((report) => (
                            <tr key={report.id}>
                              {isMain && (
                                <td className="align-middle">
                                  {getBranchName(report)}
                                </td>
                              )}
                              <td className="align-middle">
                                <div style={{ fontSize: '13px', color: '#495057' }}>
                                  {Moment(report.fromDateTime).format('DD MMM YYYY')}
                                </div>
                                <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                  {Moment(report.fromDateTime).format('HH:mm')}
                                </div>
                              </td>
                              <td className="align-middle">
                                <div style={{ fontSize: '13px', color: '#495057' }}>
                                  {Moment(report.toDateTime).format('DD MMM YYYY')}
                                </div>
                                <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                  {Moment(report.toDateTime).format('HH:mm')}
                                </div>
                              </td>
                              <td className="align-middle text-center">{formatCurrency(report.grossSales)}</td>
                              <td className="align-middle text-center">{formatCurrency(report.netSales)}</td>
                              <td className="align-middle text-center">{report.totalOrders}</td>
                              <td className="align-middle text-center">{formatCurrency(report.cashPosition)}</td>
                           
                              <td className="align-middle text-center">
                                <div className="d-flex justify-content-center gap-2">
                                  <button
                                    className="btn btn-sm"
                                    onClick={() => handleViewDetails(report)}
                                    title="View Details"
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderRadius: '6px',
                                      border: `1px solid ${themeColor}`,
                                      backgroundColor: 'transparent',
                                      color: themeColor,
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = themeColor;
                                      e.currentTarget.style.color = 'white';
                                      e.currentTarget.style.transform = 'scale(1.05)';
                                      e.currentTarget.style.boxShadow = `0 2px 8px ${themeColor}40`;
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                      e.currentTarget.style.color = themeColor;
                                      e.currentTarget.style.transform = 'scale(1)';
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}
                                  >
                                    <i className="fa fa-eye" style={{ fontSize: '12px' }}></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    pageNumber={pageNumber}
                    totalPages={totalPages}
                    setPageNumber={setPageNumber}
                    setPageSize={setPageSize}
                    themeColor={themeColor}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Auto Z-Report Schedule Modal */}
      <CustomModal
        isOpen={showAutoScheduleModal}
        onClose={() => setShowAutoScheduleModal(false)}
        title="Auto Z-Report"
        subtitle="Runs once daily at the selected time. Skipped if a Z-Report was already run today."
        modalSize="modal-md"
        isCentered={true}
        onSubmit={handleSaveAutoSchedule}
        submitButtonText={savingSchedule ? "Saving..." : "Save Schedule"}
        submitButtonDisabled={savingSchedule || scheduleLoading || requiresScheduleLocation()}
        showButton={true}
      >
        <div className="z-report-auto-schedule-modal">
          {isMain && scheduleLocationOptions.length > 0 && (
            <div className="mb-3">
              <label
                className="form-label mb-1"
                style={{ fontSize: "13px", color: isDarkMode ? "#d1d5db" : "#6c757d" }}
              >
                Branch
              </label>
              <Select
                value={scheduleLocation || scheduleLocationOptions[0]}
                onChange={handleScheduleBranchChange}
                options={scheduleLocationOptions}
                styles={dropdownStyles}
                isSearchable={false}
                isLoading={loadingLocations}
                placeholder="Select branch"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          )}

          {scheduleLoading && <Preloader />}

          <div className="mb-3">
            <ToggleSwitch
              isChecked={autoScheduleEnabled}
              onToggle={setAutoScheduleEnabled}
              label="Enable auto Z-Report"
              name="auto-z-report-enabled"
              themeColor={themeColor}
              disabled={scheduleLoading || requiresScheduleLocation()}
            />
          </div>

          <div className="mb-0">
            <MUITimePicker
              label="Daily run time"
              value={runTimeValue}
              onChange={setRunTimeValue}
              ampm={true}
              hoursOnly={true}
              disabled={scheduleLoading || requiresScheduleLocation() || !autoScheduleEnabled}
              placeholder="Select hour"
              name="auto-z-report-time"
            />
          </div>
        </div>
      </CustomModal>

      {/* Z-Report Preview/Run Modal */}
      <CustomModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        title="Z-Report Preview"
        modalSize="modal-xl"
        maxHeight="90vh"
        showButton={false}
        headerActions={
          isMain && previewLocationOptions.length > 0 ? (
            <div style={{ minWidth: "200px", maxWidth: "220px" }}>
              <label
                className="form-label mb-1"
                style={{ fontSize: "12px", color: isDarkMode ? "#d1d5db" : "#6c757d" }}
              >
                Branch
              </label>
              <Select
                value={previewLocation || previewLocationOptions[0]}
                onChange={handlePreviewBranchChange}
                options={previewLocationOptions}
                styles={dropdownStyles}
                isSearchable={false}
                isLoading={loadingLocations}
                placeholder="Select branch"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          ) : null
        }
      >
        <div className="z-report-modal-content">
          {previewLoading && (
            <div className="text-center py-4" style={{ color: isDarkMode ? "#d1d5db" : "#6c757d" }}>
              <i className="fa fa-spinner fa-spin me-2"></i>
              Loading Z-Report preview...
            </div>
          )}

          {!previewLoading && zReportData && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="z-report-timestamp">
                  <div>
                    <strong style={{ color: themeColor }}>From:</strong>{' '}
                    <span>{Moment(zReportData.fromDateTime).format('DD MMM YYYY, HH:mm')}</span>
                    {' '}
                    <strong style={{ color: themeColor, marginLeft: '15px' }}>To:</strong>{' '}
                    <span>{Moment(zReportData.toDateTime).format('DD MMM YYYY, HH:mm')}</span>
                  </div>
                </div>
                <button 
                  className="btn" 
                  onClick={() => handlePrint(zReportData)}
                  style={{
                    backgroundColor: themeColor,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    border: 'none',
                    color: 'white',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    boxShadow: `0 2px 8px ${themeColor}40`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                  }}
                >
                  <i className="fa fa-print"></i> Print
                </button>
              </div>
              {renderZReportContent(zReportData)}
            </>
          )}

          {!previewLoading && !zReportData && (
            <div className="alert alert-info text-center mb-0">
              <i className="fa fa-info-circle me-2"></i>
              No Z-Report preview data available for this branch.
            </div>
          )}
        </div>
      </CustomModal>

      {/* Z-Report Details Modal */}
      <CustomModal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="Z-Report Summary"
        modalSize="modal-xl"
        maxHeight="90vh"
        showButton={false}
      >
        <div className="z-report-modal-content">
          {selectedZReport && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="z-report-timestamp">
                  <div
                    className="mb-2"
                    style={{
                      fontSize: "18px",
                      fontWeight: 600,
                      color: isDarkMode ? "#ffffff" : "#212529",
                    }}
                  >
                    {getBranchName(selectedZReport)}
                  </div>
                  <div>
                    <strong style={{ color: themeColor }}>From:</strong>{' '}
                    <span>{Moment(selectedZReport.fromDateTime).format('DD MMM YYYY, HH:mm')}</span>
                    {' '}
                    <strong style={{ color: themeColor, marginLeft: '15px' }}>To:</strong>{' '}
                    <span>{Moment(selectedZReport.toDateTime).format('DD MMM YYYY, HH:mm')}</span>
                  </div>
                </div>
                <button 
                  className="btn" 
                  onClick={() => handlePrint(selectedZReport)}
                  style={{
                    backgroundColor: themeColor,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    border: 'none',
                    color: 'white',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    boxShadow: `0 2px 8px ${themeColor}40`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                  }}
                >
                  <i className="fa fa-print"></i> Print
                </button>
              </div>
              {renderZReportContent(selectedZReport)}
            </>
          )}
        </div>
      </CustomModal>
    </div>
  );
};

export default ZReport;
